import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiMail, FiPhone, FiMapPin, FiBuilding, FiCalendar, FiDollarSign, FiUser, FiClock, FiEdit, FiTrash2 } = FiIcons;

const ClientDetailModal = ({ isOpen, onClose, client, onEdit, onDelete }) => {
  if (!client) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800 border-green-200';
      case 'Inactive': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'Pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Overdue': return 'bg-red-100 text-red-800 border-red-200';
      case 'Paused': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Completed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'Suspended': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatCurrency = (amount) => {
    if (typeof amount === 'number') {
      return `$${amount.toLocaleString()}`;
    }
    return amount || '$0';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          <motion.div
            className="relative bg-white rounded-xl w-full max-w-5xl max-h-[95vh] overflow-y-auto shadow-xl"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 rounded-t-xl z-10">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold">{client.name}</h2>
                  <p className="text-blue-100 text-lg">{client.business_name}</p>
                  <div className="flex items-center space-x-3 mt-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(client.status)} bg-white`}>
                      {client.status}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${
                      client.gst_type === 'B2B' ? 'bg-blue-100 text-blue-800 border-blue-200' : 'bg-purple-100 text-purple-800 border-purple-200'
                    } bg-white`}>
                      {client.gst_type}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onEdit(client)}
                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors duration-200"
                  >
                    <SafeIcon icon={FiEdit} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => onDelete(client.id)}
                    className="p-2 hover:bg-red-500 hover:bg-opacity-20 rounded-lg transition-colors duration-200"
                  >
                    <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors duration-200"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-8">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center border border-green-200">
                  <p className="text-2xl font-bold text-green-800">{formatCurrency(client.monthly_revenue)}</p>
                  <p className="text-sm text-green-600">Monthly Revenue</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center border border-blue-200">
                  <p className="text-2xl font-bold text-blue-800">{formatCurrency(client.total_billed)}</p>
                  <p className="text-sm text-blue-600">Total Billed</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 text-center border border-purple-200">
                  <p className="text-2xl font-bold text-purple-800">{client.projects || 0}</p>
                  <p className="text-sm text-purple-600">Active Projects</p>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 text-center border border-orange-200">
                  <p className="text-2xl font-bold text-orange-800">{client.gst_rate || 0}%</p>
                  <p className="text-sm text-orange-600">GST Rate</p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiUser} className="w-5 h-5 mr-2 text-blue-600" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-700 border-b border-gray-300 pb-2">Primary Contact</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-700 font-medium">{client.name}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-400" />
                        <a href={`mailto:${client.email}`} className="text-blue-600 hover:text-blue-800">{client.email}</a>
                      </div>
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiPhone} className="w-4 h-4 text-gray-400" />
                        <a href={`tel:${client.phone}`} className="text-blue-600 hover:text-blue-800">{client.phone}</a>
                      </div>
                      {client.address && (
                        <div className="flex items-start space-x-3">
                          <SafeIcon icon={FiMapPin} className="w-4 h-4 text-gray-400 mt-1" />
                          <span className="text-gray-600">{client.address}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-700 border-b border-gray-300 pb-2">Billing Contact</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-700">{client.billing_contact?.name || 'Same as primary'}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-400" />
                        <a href={`mailto:${client.billing_contact?.email || client.email}`} className="text-blue-600 hover:text-blue-800">
                          {client.billing_contact?.email || client.email}
                        </a>
                      </div>
                      <div className="flex items-center space-x-3">
                        <SafeIcon icon={FiPhone} className="w-4 h-4 text-gray-400" />
                        <a href={`tel:${client.billing_contact?.phone || client.phone}`} className="text-blue-600 hover:text-blue-800">
                          {client.billing_contact?.phone || client.phone}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* GST Information */}
              <div className="bg-green-50 rounded-lg p-6 border border-green-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiBuilding} className="w-5 h-5 mr-2 text-green-600" />
                  GST Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-green-300">
                    <div className="flex items-center space-x-2 mb-2">
                      <SafeIcon icon={FiBuilding} className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-gray-700">GST Type</span>
                    </div>
                    <p className="text-gray-800 font-semibold">{client.gst_type}</p>
                  </div>
                  
                  {client.gstin && (
                    <div className="bg-white rounded-lg p-4 border border-green-300">
                      <div className="flex items-center space-x-2 mb-2">
                        <SafeIcon icon={FiBuilding} className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-gray-700">GSTIN</span>
                      </div>
                      <p className="text-gray-800 font-mono text-sm">{client.gstin}</p>
                    </div>
                  )}
                  
                  <div className="bg-white rounded-lg p-4 border border-green-300">
                    <div className="flex items-center space-x-2 mb-2">
                      <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-gray-700">GST Rate</span>
                    </div>
                    <p className="text-gray-800 font-semibold">{client.gst_rate}%</p>
                  </div>
                </div>
              </div>

              {/* Contract Details */}
              <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2 text-purple-600" />
                  Contract Details
                </h3>
                <div className="bg-white rounded-lg p-4 space-y-4 border border-purple-300">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4 text-purple-600" />
                      <span className="font-medium text-gray-700">Contract Period</span>
                    </div>
                    <span className="text-gray-800 font-medium">
                      {formatDate(client.contract_details?.start_date)} - {formatDate(client.contract_details?.end_date)}
                    </span>
                  </div>
                  {client.contract_details?.renewal_terms && (
                    <div>
                      <span className="font-medium text-gray-700">Renewal Terms:</span>
                      <p className="text-gray-600 mt-1 bg-gray-50 p-3 rounded">{client.contract_details.renewal_terms}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Service History */}
              <div className="bg-orange-50 rounded-lg p-6 border border-orange-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiSettings} className="w-5 h-5 mr-2 text-orange-600" />
                  Service History
                </h3>
                <div className="space-y-3">
                  {client.service_history && client.service_history.length > 0 ? (
                    client.service_history.map((service, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-white rounded-lg border border-orange-300">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-800">{service.service}</h4>
                          <p className="text-sm text-gray-600">Started: {formatDate(service.start_date)}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(service.status)}`}>
                          {service.status}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No service history available
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Terms */}
              <div className="bg-red-50 rounded-lg p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiDollarSign} className="w-5 h-5 mr-2 text-red-600" />
                  Payment Terms
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-red-300">
                    <div className="flex items-center space-x-2 mb-2">
                      <SafeIcon icon={FiClock} className="w-4 h-4 text-red-600" />
                      <span className="font-medium text-gray-700">Payment Due</span>
                    </div>
                    <p className="text-gray-800 font-semibold">{client.payment_terms?.due_days || 30} days</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-red-300">
                    <div className="flex items-center space-x-2 mb-2">
                      <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-red-600" />
                      <span className="font-medium text-gray-700">Early Discount</span>
                    </div>
                    <p className="text-gray-800 font-semibold">{client.payment_terms?.early_payment_discount || 0}%</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-red-300">
                    <div className="flex items-center space-x-2 mb-2">
                      <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-red-600" />
                      <span className="font-medium text-gray-700">Late Penalty</span>
                    </div>
                    <p className="text-gray-800 font-semibold">{client.payment_terms?.late_payment_penalty || 0}%</p>
                  </div>
                </div>
              </div>

              {/* Client Segmentation */}
              <div className="bg-indigo-50 rounded-lg p-6 border border-indigo-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiTarget} className="w-5 h-5 mr-2 text-indigo-600" />
                  Client Segmentation
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-indigo-300">
                    <span className="font-medium text-gray-700">Primary Service:</span>
                    <p className="text-gray-800 mt-1 font-semibold">{client.segmentation?.service_type || 'Not assigned'}</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-indigo-300">
                    <span className="font-medium text-gray-700">Billing Frequency:</span>
                    <p className="text-gray-800 mt-1 font-semibold">{client.segmentation?.billing_frequency || 'Monthly'}</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-indigo-300">
                    <span className="font-medium text-gray-700">Account Manager:</span>
                    <p className="text-gray-800 mt-1 font-semibold">{client.segmentation?.account_manager || 'Not assigned'}</p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-indigo-300">
                    <span className="font-medium text-gray-700">Payment Status:</span>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-1 border ${getStatusColor(client.segmentation?.payment_status || 'Active')}`}>
                      {client.segmentation?.payment_status || 'Active'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Created:</span> {formatDate(client.created_at)}
                  </div>
                  <div>
                    <span className="font-medium">Last Updated:</span> {formatDate(client.updated_at)}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ClientDetailModal;